import { TANK_SERIES_RESOLUTION_LABELS } from "@station/contracts";
import { Decimal } from "@station/core";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  FormField,
  QuantityText,
  Select,
  SkeletonRows,
  StatusBadge,
  type Tone,
} from "@station/ui";
import { createFileRoute } from "@tanstack/react-router";
import { Droplets, Gauge, LineChart } from "lucide-react";
import { type ReactElement, useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { tankSeriesChartOption } from "../../../features/fuel/chart-options.js";
import { remainingTime, runsOutSoon } from "../../../features/fuel/forecast-values.js";
import {
  isRangeTooLarge,
  useFuelPermissions,
  useTanks,
  useTankSeries,
} from "../../../features/fuel/queries.js";
import { TankReadingDialog } from "../../../features/fuel/reading-dialogs.js";
import {
  ChartWithTable,
  FuelPage,
  fuelSearchShape,
  NoStations,
  StationField,
  Th,
  useFuelStation,
} from "../../../features/fuel/shared.js";
import { TankGauge } from "../../../features/fuel/tank-gauge.js";
import {
  fallbackResolution,
  isStaleReading,
  minutesSince,
  readingAge,
  rangeWindow,
  resolutionForRange,
  TANK_RANGES,
  type TankRange,
  tankGaugeGeometry,
  tankLevelFlags,
  type TankResolution,
} from "../../../features/fuel/tank-values.js";
import { Field, Section } from "../../../features/expenses/shared.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDateTime } from "../../../lib/format.js";

const searchSchema = z.object({
  ...fuelSearchShape,
  tank: z.string().uuid().optional().catch(undefined),
  range: z.enum(TANK_RANGES).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/akaryakit/tanklar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: TanksPage,
});

type TankStatusItem = NonNullable<ReturnType<typeof useTanks>["data"]>["items"][number];

function TanksPage(): ReactElement {
  const { t } = useTranslation();
  const permissions = useFuelPermissions();
  return (
    <FuelPage
      title={t("menu.fuelTanks")}
      description={t("fuel.tanks.description")}
      breadcrumb={[{ label: t("menu.fuelTanks") }]}
    >
      {({ companyId }) =>
        permissions.canRead ? (
          <TanksBody companyId={companyId} />
        ) : (
          <Alert tone="info">{t("fuel.noPermission")}</Alert>
        )
      }
    </FuelPage>
  );
}

function TanksBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const permissions = useFuelPermissions();
  const station = useFuelStation(companyId);
  const tanks = useTanks(station.stationId);
  const [readingTank, setReadingTank] = useState<TankStatusItem | null>(null);
  const headingId = useId();

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stationId === null) return <NoStations />;

  const items = tanks.data?.items ?? [];
  const selected = items.find((tank) => tank.tankId === search.tank) ?? null;
  const range: TankRange = search.range ?? "24h";

  return (
    <>
      {station.showPicker ? (
        <Card className="flex flex-wrap items-end gap-3 p-4">
          <StationField selection={station} />
        </Card>
      ) : null}

      <section aria-labelledby={headingId} className="grid gap-3">
        <h2 id={headingId} className="text-base font-semibold">
          {t("fuel.tanks.title")}
        </h2>
        {tanks.error ? (
          <Alert tone="danger">{errorMessage(tanks.error)}</Alert>
        ) : !tanks.data ? (
          <SkeletonRows rows={4} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Droplets />}
            title={t("fuel.tanks.empty")}
            description={t("fuel.tanks.emptyHint")}
          />
        ) : (
          <ul className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
            {items.map((tank) => (
              <li key={tank.tankId}>
                <TankCard
                  tank={tank}
                  selected={tank.tankId === search.tank}
                  canCreateReading={permissions.canCreateReading}
                  onSelect={() =>
                    void navigate({
                      search: (previous) => ({ ...previous, tank: tank.tankId }),
                      replace: true,
                    })
                  }
                  onEnterReading={() => setReadingTank(tank)}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      {selected ? (
        <TankSeriesSection
          tank={selected}
          range={range}
          onRangeChange={(next) =>
            void navigate({ search: (previous) => ({ ...previous, range: next }), replace: true })
          }
        />
      ) : null}

      <TankReadingDialog
        tank={
          readingTank
            ? {
                id: readingTank.tankId,
                tankNo: readingTank.tankNo,
                productName: readingTank.productName,
              }
            : null
        }
        open={readingTank !== null}
        onOpenChange={(open) => {
          if (!open) setReadingTank(null);
        }}
      />
    </>
  );
}

const FLAG_TONE: Record<string, Tone> = {
  CRITICAL: "danger",
  BELOW_MIN: "warning",
  ABOVE_SAFE_FILL: "warning",
};

function litres(value: string, scale = 3): string {
  return `${Decimal.from(value).formatTr(scale)} L`;
}

function TankCard({
  tank,
  selected,
  canCreateReading,
  onSelect,
  onEnterReading,
}: {
  tank: TankStatusItem;
  selected: boolean;
  canCreateReading: boolean;
  onSelect: () => void;
  onEnterReading: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();
  const geometry = tankGaugeGeometry(tank);
  const flags = tankLevelFlags(tank);
  const reading = tank.lastReading;
  const ageMinutes = reading ? minutesSince(reading.measuredAt, new Date()) : null;
  const age = ageMinutes === null ? null : readingAge(ageMinutes);
  const fillPercentText = tank.fillPercent === null ? "—" : Decimal.from(tank.fillPercent).formatTr(1);

  return (
    <Card
      className={`grid h-full gap-3 p-4 ${selected ? "ring-2 ring-[var(--ring)]" : ""}`}
      aria-labelledby={headingId}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 id={headingId} className="text-base font-semibold">
          {t("fuel.tanks.tankTitle", { tankNo: tank.tankNo, product: tank.productName })}
        </h3>
        <div className="flex flex-wrap gap-1.5">
          {tank.status === "ACTIVE" ? null : (
            <StatusBadge tone="neutral">{t(`fuel.tanks.status.${tank.status}`)}</StatusBadge>
          )}
          {flags.map((flag) => (
            <StatusBadge key={flag} tone={FLAG_TONE[flag] ?? "neutral"}>
              {t(`fuel.tanks.flags.${flag}`)}
            </StatusBadge>
          ))}
          {ageMinutes !== null && isStaleReading(ageMinutes) ? (
            <StatusBadge tone="warning">{t("fuel.tanks.stale")}</StatusBadge>
          ) : null}
        </div>
      </div>

      <div className="flex gap-4">
        <TankGauge
          geometry={geometry}
          color={tank.productColor}
          ariaLabel={t("fuel.tanks.gaugeLabel", {
            tankNo: tank.tankNo,
            product: tank.productName,
            percent: fillPercentText,
            volume: reading ? litres(reading.grossVolumeL) : t("fuel.tanks.noReading"),
          })}
        />
        <dl className="grid flex-1 gap-3 text-sm sm:grid-cols-2">
          <Field label={t("fuel.tanks.volume")}>{reading ? litres(reading.grossVolumeL) : "—"}</Field>
          <Field label={t("fuel.tanks.capacity")}>{litres(tank.nominalCapacityL, 0)}</Field>
          <Field label={t("fuel.tanks.fillPercent")}>
            {tank.fillPercent === null ? "—" : `%${fillPercentText}`}
          </Field>
          <Field label={t("fuel.tanks.netVolume")}>
            {reading?.netVolumeL ? litres(reading.netVolumeL) : "—"}
          </Field>
          <Field label={t("fuel.tanks.temperature")}>
            <QuantityText value={reading?.temperatureC ?? null} unit="CELSIUS" />
          </Field>
          <Field label={t("fuel.tanks.water")}>
            <QuantityText value={reading?.waterMm ?? null} unit="MM" />
          </Field>
          {tank.isLpg ? (
            <Field label={t("fuel.tanks.mass")}>
              <QuantityText value={reading?.massKg ?? null} unit="KG" />
            </Field>
          ) : null}
          <Field label={t("fuel.tanks.safeFill")}>{litres(tank.safeFillVolumeL, 0)}</Field>
          <Field label={t("fuel.tanks.minLevel")}>{litres(tank.minLevelL, 0)}</Field>
          <Field label={t("fuel.tanks.lastReading")}>
            {reading ? (
              <span className="flex flex-col">
                <span>{formatDateTime(reading.measuredAt)}</span>
                {age ? (
                  <span className="text-xs text-muted-foreground">
                    {t(`fuel.tanks.age.${age.unit}`, { count: age.value })}
                  </span>
                ) : null}
              </span>
            ) : (
              t("fuel.tanks.noReading")
            )}
          </Field>
          <Field label={t("fuel.tanks.runOut")}>
            <RunOutValue at={tank.estimatedRunOutAt} />
          </Field>
        </dl>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={onSelect} aria-pressed={selected}>
          <LineChart aria-hidden="true" />
          {t("fuel.tanks.showChart")}
        </Button>
        {canCreateReading ? (
          <Button variant="outline" size="sm" onClick={onEnterReading}>
            <Gauge aria-hidden="true" />
            {t("fuel.tanks.enterReading")}
          </Button>
        ) : null}
      </div>
    </Card>
  );
}

/**
 * Tahmini bitiş. 24 saatten yakın (ya da hesapça geçmiş) bitiş UYARI tonundadır; renk tek taşıyıcı
 * olmasın diye altına metin de yazılır (WCAG 1.4.1).
 */
function RunOutValue({ at }: { at: string | null }): ReactElement {
  const { t } = useTranslation();
  const now = useMemo(() => new Date(), []);
  if (at === null) return <span className="text-muted-foreground">{formatDateTime(null)}</span>;
  const remaining = remainingTime(at, now);
  const soon = runsOutSoon(at, now);
  return (
    <span className="grid gap-0.5">
      <span className={soon ? "font-semibold text-warning" : undefined}>{formatDateTime(at)}</span>
      <span className="text-xs text-muted-foreground">
        {remaining === null
          ? ""
          : remaining.past
            ? t("fuel.tanks.runOutPast")
            : soon
              ? t("fuel.tanks.runOutSoon", { hours: remaining.totalHours })
              : t("fuel.tanks.runOutIn", { days: remaining.days, hours: remaining.hours })}
      </span>
    </span>
  );
}

function TankSeriesSection({
  tank,
  range,
  onRangeChange,
}: {
  tank: TankStatusItem;
  range: TankRange;
  onRangeChange: (range: TankRange) => void;
}): ReactElement {
  const { t } = useTranslation();
  const wanted = resolutionForRange(range);
  const [downgraded, setDowngraded] = useState(false);
  const resolution: TankResolution = downgraded ? (fallbackResolution(wanted) ?? wanted) : wanted;
  // Aralık değişince ham çözünürlük yeniden denenir (yeni aralık sığabilir).
  useEffect(() => setDowngraded(false), [range]);

  // `rangeWindow` her render'da yeni zaman üretir; sorgu anahtarı sabit kalsın diye aralığa bağlanır.
  const window = useMemo(() => rangeWindow(range, new Date()), [range]);
  const series = useTankSeries(tank.tankId, window, resolution);
  useEffect(() => {
    if (isRangeTooLarge(series.error)) setDowngraded(true);
  }, [series.error]);

  const points = useMemo(() => series.data?.points ?? [], [series.data]);
  const option = useMemo(
    () =>
      tankSeriesChartOption({
        points,
        productColor: tank.productColor,
        safeFillVolumeL: tank.safeFillVolumeL,
        minLevelL: tank.minLevelL,
        daily: resolution === "DAILY",
        labels: {
          gross: t("fuel.series.gross"),
          net: t("fuel.series.net"),
          temperature: t("fuel.series.temperature"),
          volumeAxis: t("fuel.series.volumeAxis"),
          temperatureAxis: t("fuel.series.temperatureAxis"),
          safeFill: t("fuel.series.safeFill"),
          minLevel: t("fuel.series.minLevel"),
        },
      }),
    [points, tank.productColor, tank.safeFillVolumeL, tank.minLevelL, resolution, t],
  );

  const chartLabel = t("fuel.series.ariaLabel", {
    tankNo: tank.tankNo,
    product: tank.productName,
    range: t(`fuel.series.ranges.${range}`),
  });

  return (
    <Section
      title={t("fuel.series.title", { tankNo: tank.tankNo, product: tank.productName })}
      actions={
        <FormField label={t("fuel.series.range")} className="w-44">
          {(control) => (
            <Select
              {...control}
              value={range}
              onChange={(event) => onRangeChange(event.target.value as TankRange)}
            >
              {TANK_RANGES.map((value) => (
                <option key={value} value={value}>
                  {t(`fuel.series.ranges.${value}`)}
                </option>
              ))}
            </Select>
          )}
        </FormField>
      }
    >
      <p className="text-xs text-muted-foreground">
        {t("fuel.series.resolution", { resolution: TANK_SERIES_RESOLUTION_LABELS[resolution] })}
      </p>
      {downgraded ? <Alert tone="info">{t("fuel.series.downgraded")}</Alert> : null}
      {series.error && !isRangeTooLarge(series.error) ? (
        <Alert tone="danger">{errorMessage(series.error)}</Alert>
      ) : !series.data ? (
        <SkeletonRows rows={5} />
      ) : points.length === 0 ? (
        <EmptyState title={t("fuel.series.empty")} description={t("fuel.series.emptyHint")} />
      ) : (
        <ChartWithTable
          ariaLabel={chartLabel}
          option={option}
          height={340}
          tableLabel={t("fuel.series.tableLabel")}
        >
          <table className="w-full text-sm">
            <caption className="sr-only">{t("fuel.series.tableLabel")}</caption>
            <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr>
                <Th>{t("fuel.series.fields.at")}</Th>
                <Th align="right">{t("fuel.series.fields.grossVolume")}</Th>
                <Th align="right">{t("fuel.series.fields.netVolume")}</Th>
                <Th align="right">{t("fuel.series.fields.level")}</Th>
                <Th align="right">{t("fuel.series.fields.water")}</Th>
                <Th align="right">{t("fuel.series.fields.temperature")}</Th>
              </tr>
            </thead>
            <tbody>
              {points.map((point) => (
                <tr key={point.at} className="border-t">
                  <td className="px-3 py-2">{formatDateTime(point.at)}</td>
                  <td className="px-3 py-2 text-right">
                    <QuantityText value={point.grossVolumeL} unit="LITRE" />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <QuantityText value={point.netVolumeL} unit="LITRE" />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <QuantityText value={point.levelMm} unit="MM" />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <QuantityText value={point.waterMm} unit="MM" />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <QuantityText value={point.temperatureC} unit="CELSIUS" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ChartWithTable>
      )}
    </Section>
  );
}
